import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { queryKeysDeliveryNote } from '../queryKeys';

import { bulkApproveDlnItems } from './bulkApproveDlnItems';
import {
  type BulkApproveDlnItemsMutationArguments,
  type BulkApproveDlnItemsResponse,
} from './types';

/**
 * React Query mutation hook for bulk-approving delivery note items.
 *
 * @param options - Optional TanStack Query mutation options for customizing behavior.
 * @returns A mutation object with methods to trigger the bulk approval.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/bulk_approve_dln_items_delivery_note__asset_id__item_bulk_approve_put
 */
export const useMutationBulkApproveDlnItems = (
  options?: Omit<
    UseMutationOptions<
      BulkApproveDlnItemsResponse,
      Error,
      BulkApproveDlnItemsMutationArguments
    >,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  const { onSettled, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: ({ assetId, payload }: BulkApproveDlnItemsMutationArguments) =>
      bulkApproveDlnItems(assetId, payload),
    onSettled: (data, error, variables, onMutateResult, context) => {
      if (variables?.assetId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeysDeliveryNote.getInternal(variables.assetId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeysDeliveryNote.get(variables.assetId),
        });
      }

      void onSettled?.(data, error, variables, onMutateResult, context);
    },
    ...restOptions,
  });
};
